import { Snackbar, SnackbarProps } from '@mui/base/Snackbar';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Transition } from 'react-transition-group';

const positioningStyles = {
  entering: 'translateX(0)',
  entered: 'translateX(0)',
  exiting: 'translateX(500px)',
  exited: 'translateX(500px)',
  unmounted: 'translateX(500px)'
};

interface ErrorSnackbarProps extends SnackbarProps {
  error: unknown;
}

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

export const ErrorSnackbar: FC<ErrorSnackbarProps> = props => {
  const { error, ...otherProps } = props;
  const nodeRef = useRef(null);
  const { t } = useTranslation();

  const [exited, setExited] = useState(true);
  const handleOnEnter = useCallback(() => {
    setExited(false);
  }, []);
  const handleOnExited = useCallback(() => {
    setExited(true);
  }, []);

  // TODO: fix API transformResponseError to return an error object
  const { title, message } = useMemo(() => {
    if (typeof error === 'string') {
      return { message: error };
    }

    if (!isFetchBaseQueryError(error)) {
      return { title: t('error.occurred'), message: t('error.unknown') };
    }

    if (error.status === 'FETCH_ERROR') {
      return {
        title: t('error.network.title'),
        message: t('error.network.message')
      };
    }
    if (error.status === 'TIMEOUT_ERROR') {
      return {
        title: t('error.timeout.title'),
        message: t('error.timeout.message')
      };
    }
    if (error.status === 'PARSING_ERROR') {
      return {
        title: t('error.parsing.title'),
        message: t('error.parsing.message')
      };
    }

    // Numeric status — backend responded with an HTTP error
    const msg = (error.data as { message?: string } | undefined)?.message;
    return { title: t('error.occurred'), message: msg ?? t('error.unknown') };
  }, [error, t]);

  return (
    <Snackbar
      {...otherProps}
      autoHideDuration={5000}
      exited={exited}
      className="fixed bottom-4 left-auto right-4 z-50 flex max-w-xl font-sans"
    >
      <Transition
        timeout={{ enter: 400, exit: 400 }}
        in={props.open}
        appear
        unmountOnExit
        onEnter={handleOnEnter}
        onExited={handleOnExited}
        nodeRef={nodeRef}
      >
        {status => (
          <div
            className="flex gap-4	overflow-hidden	rounded-lg border border-solid	border-slate-200 bg-white p-3 text-start text-slate-900 shadow-md dark:border-slate-700 dark:bg-slate-900	dark:text-slate-50"
            style={{
              transform: positioningStyles[status],
              transition: 'transform 300ms ease'
            }}
            ref={nodeRef}
          >
            <div className="max-w-full flex-1">
              <p className="m-0 mr-2 font-medium leading-normal">{title}</p>
              <p className="m-0 font-normal leading-normal text-slate-800 dark:text-slate-400">
                {message}
              </p>
            </div>
          </div>
        )}
      </Transition>
    </Snackbar>
  );
};
